import React, { useState } from 'react'
import { createReturn } from '@api/returns'
import { useRouter } from 'next/router'
import { checkStoreIs, getLink, prefixLocale } from '@helpers/env'
import trackError from '@helpers/trackError'
import useSessionStorage from '@helpers/hooks/useSessionStorage'
import Translations from '@helpers/translations'
import { returnsAnalytics } from '@components/returns/helpers'
import { ReturnsHeaderImage } from '@components/returns/ReturnsHeaderImage'
import PageLoading from '@components/Loading/PageLoading'
import Notification, { NotificationProps } from '@components/ui/Notification'
import ReturnsBanner from '../ReturnsBanner'
import DeliveryOptions from './DeliveryOptions'
import styles from '../ItemsSelection/styles.module.scss'
import { Carrier, Carriers, ReboundCustomerAddress } from '@api/returns/types'
import { EmptyObject } from 'types/helpers'
import { ItemSelectionOnSubmitProps } from '../ItemsSelection'
import { mapProductsToReturnedProducts } from '@api/returns/mappers/mapProductsToReturnProducts'
import { JACKET_SKU_PREFIXES } from '@consts/index'

export const DELIVERY_METHOD_WITH_DATE_DROPDOWN = [330]

type CarrierSelectionProps = {
  orderNumber: string
  postcode: string
  headerImage: {
    desktop: string
    mobile: string
  }
  carriers: Carrier[]
  productsToReturn: ItemSelectionOnSubmitProps['productsToReturn']
  salesOrderId?: string
  customerAddress: ReboundCustomerAddress
}

export type CollectionDate = { collectionDate?: string }

type GetSubmitButtonStatusArgs = {
  id: number | string | undefined
} & CollectionDate

function getSubmitButtonStatus({
  id,
  collectionDate
}: GetSubmitButtonStatusArgs) {
  const isDeliveryMethodWithDateDropdown =
    DELIVERY_METHOD_WITH_DATE_DROPDOWN.includes(Number(id))
  return (
    (!!id && !isDeliveryMethodWithDateDropdown) ||
    (isDeliveryMethodWithDateDropdown && !!collectionDate)
  )
}

function isJacketSku(sku?: string) {
  if (!sku) return false
  return JACKET_SKU_PREFIXES.some(prefix => sku.startsWith(prefix))
}

export function getFilteredCarriers(
  carriers: Carriers,
  productsToReturn: ItemSelectionOnSubmitProps['productsToReturn']
) {
  const isGBStore = checkStoreIs('gb')

  if (!isGBStore) return carriers

  const hasJacketToReturn = productsToReturn?.some(product =>
    isJacketSku(product.sku)
  )

  return (
    carriers
      // hide DPD for Jackets
      .filter(carrier => {
        if (!hasJacketToReturn) return true
        return [6030, 6031].includes(carrier.id)
      })
  )
}

const CarrierSelection = ({
  orderNumber,
  postcode,
  headerImage,
  carriers,
  productsToReturn,
  salesOrderId,
  customerAddress
}: CarrierSelectionProps) => {
  const router = useRouter()
  const setHasShownReturnsNoteModal = useSessionStorage(
    'hasShownReturnsNoteModal',
    true
  )[1]
  const filteredCarriers = getFilteredCarriers(carriers, productsToReturn)
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<
    (Carrier & CollectionDate) | EmptyObject
  >(filteredCarriers[0] || {})
  const [notificationError, setNotificationError] =
    useState<NotificationProps | null>(null)

  const handleCreateReturn = async () => {
    if (!salesOrderId) {
      trackError(new Error('createReturn failed'), {
        error: new Error('salesOrderId missing from return')
      })
      return
    }

    try {
      const response = await createReturn({
        carrierServiceId: selectedDeliveryMethod.id.toString(),
        orderName: `#${orderNumber}`,
        salesOrderId,
        customerAddress,
        returnedProducts: mapProductsToReturnedProducts(productsToReturn || [])
      })

      setHasShownReturnsNoteModal(false)

      returnsAnalytics('Return Label Generated', {
        carrier: selectedDeliveryMethod?.title,
        return_description: selectedDeliveryMethod?.byline,
        order_id: orderNumber
      })
      const { query } = router

      router.replace(
        `${prefixLocale()}/pages?pagesHandle=returns&order_number=${orderNumber}&postcode=${postcode}&successReturn=${response.data?.returnId}`,
        `${prefixLocale()}/pages/returns?order_number=${orderNumber}&postcode=${postcode}&successReturn=${
          response.data?.returnId
        }${query.admin ? '&admin=true' : ''}`,
        { shallow: true }
      )
    } catch (error) {
      setNotificationError({
        type: 'error',
        content: (
          <span>
            {Translations.localise(
              'pages.returns.returns-notifications.return-default.copy-1'
            )}{' '}
            <a style={{ color: '#DC1E32' }} href={getLink('/pages/contact-us')}>
              {Translations.localise(
                'pages.returns.returns-notifications.return-default.copy-2'
              )}
            </a>{' '}
            {Translations.localise(
              'pages.returns.returns-notifications.return-default.copy-3'
            )}
          </span>
        )
      })
      trackError(new Error('createReturn failed'), { error: error as Error })
    }
  }

  const shouldShowSubmitButton =
    !!filteredCarriers?.length && !!selectedDeliveryMethod?.id

  const isSubmitButtonActive = getSubmitButtonStatus({
    id: selectedDeliveryMethod.id,
    collectionDate: selectedDeliveryMethod.collectionDate
  })

  return (
    <section className={styles.return}>
      <ReturnsHeaderImage
        content={{
          title: Translations.localise('pages.returns.select-courier'),
          byline: [`Step 2/2`],
          image: headerImage
        }}
      />
      {notificationError?.type && <Notification {...notificationError} />}
      {!filteredCarriers?.length ? (
        <PageLoading />
      ) : (
        <>
          <DeliveryOptions
            deliveryMethods={filteredCarriers}
            carrierId={selectedDeliveryMethod?.id || filteredCarriers[0].id}
            onChangeDeliveryMethod={setSelectedDeliveryMethod}
            selectedDeliveryMethod={selectedDeliveryMethod}
          />
          <ReturnsBanner
            active={shouldShowSubmitButton}
            buttonActive={isSubmitButtonActive}
            type="deliveryMethod"
            onAction={handleCreateReturn}
            copy={`${selectedDeliveryMethod?.title} ${Translations.localise(
              'pages.returns.return-selected'
            )}`}
          />
        </>
      )}
    </section>
  )
}

export default CarrierSelection
